import { RefObject, useEffect, useState } from "react";
import { ObjectDetectionFrameWithLabelName } from "types/reId/ObjectDetectionFrameWithLabelName";
import { EntityId } from "@reduxjs/toolkit";
import { ObjectDetectionFrame } from "types/reId/ObjectDetectionFrame";

export interface BoundingBoxOverlayProps {
    detectedObjects: ObjectDetectionFrameWithLabelName[];
    onDetectedObjectClick: (detectedObject: ObjectDetectionFrame) => void;
    /**
     * Ref to the element wrapping the video player. The overlay reads the
     * native video resolution from the <video> element inside it so the pixel
     * based bounding boxes can be mapped onto the rendered video.
     */
    containerRef: RefObject<HTMLElement | null>;
}

interface VideoSize {
    width: number;
    height: number;
}

interface VideoBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Draws the bounding boxes of detected objects over the video.
 *
 * The bounding box points are expressed in the native pixel coordinates of the
 * video. An SVG with a viewBox equal to the native resolution and
 * preserveAspectRatio="none" lets those pixel values be used directly while the
 * browser scales them to the rendered video size.
 */
export function BoundingBoxOverlay(props: BoundingBoxOverlayProps) {
    const [videoSize, setVideoSize] = useState<VideoSize | null>(null);
    const [videoBounds, setVideoBounds] = useState<VideoBounds | null>(null);
    const [hoveredObjectId, setHoveredObjectId] = useState<EntityId | null>(
        null,
    );

    useEffect(() => {
        const container = props.containerRef.current;
        if (!container) return;

        const video = container.querySelector("video");
        if (!video) return;

        const updateSizeAndBounds = () => {
            if (video.videoWidth && video.videoHeight) {
                setVideoSize({
                    width: video.videoWidth,
                    height: video.videoHeight,
                });
            }

            const containerRect = container.getBoundingClientRect();
            const videoRect = video.getBoundingClientRect();

            const leftDiff = videoRect.left - containerRect.left;
            const topDiff = videoRect.top - containerRect.top;

            if (
                videoRect.left !== containerRect.left ||
                videoRect.top !== containerRect.top
            ) {
                console.log("videoSizeLeft Diff", leftDiff);
                console.log("videoSizeTop Diff", topDiff);
            }
            setVideoBounds({
                left: leftDiff,
                top: topDiff,
                width: videoRect.width,
                height: videoRect.height,
            });
        };

        updateSizeAndBounds();

        video.addEventListener("loadedmetadata", updateSizeAndBounds);
        video.addEventListener("resize", updateSizeAndBounds);
        window.addEventListener("resize", updateSizeAndBounds);

        const resizeObserver = new ResizeObserver(updateSizeAndBounds);
        resizeObserver.observe(container);
        resizeObserver.observe(video);

        return () => {
            video.removeEventListener("loadedmetadata", updateSizeAndBounds);
            video.removeEventListener("resize", updateSizeAndBounds);
            window.removeEventListener("resize", updateSizeAndBounds);
            resizeObserver.disconnect();
        };
    }, [props.containerRef]);

    if (!videoSize || !videoBounds) return null;

    return (
        <svg
            viewBox={`0 0 ${videoSize.width} ${videoSize.height}`}
            preserveAspectRatio="none"
            style={{
                position: "absolute",
                left: videoBounds.left,
                top: videoBounds.top,
                width: videoBounds.width,
                height: videoBounds.height,
                pointerEvents: "all",
                cursor: "default",
            }}
        >
            {props.detectedObjects.map((detectedObject) => {
                const { upperRight, lowerLeft } = detectedObject.bbox;
                const isHovered =
                    hoveredObjectId ===
                    `${detectedObject.persistedRecordingId} + ${detectedObject.trackerId}`;
                const isActive = isHovered;
                const strokeColor = "#00ff00";
                const fillOpacity = isActive ? 0.2 : 0;

                const x = Math.min(lowerLeft.x, upperRight.x);
                const y = Math.min(lowerLeft.y, upperRight.y);
                const width = Math.abs(upperRight.x - lowerLeft.x);
                const height = Math.abs(upperRight.y - lowerLeft.y);

                return (
                    <g
                        key={`${detectedObject.persistedRecordingId} + ${detectedObject.trackerId}`}
                    >
                        <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fillOpacity={fillOpacity}
                            stroke={strokeColor}
                            strokeWidth={2}
                            style={{ pointerEvents: "all", cursor: "pointer" }}
                            onMouseEnter={() =>
                                setHoveredObjectId(
                                    `${detectedObject.persistedRecordingId} + ${detectedObject.trackerId}`,
                                )
                            }
                            onMouseLeave={() => setHoveredObjectId(null)}
                            onClick={() =>
                                props.onDetectedObjectClick(detectedObject)
                            }
                        />
                        <text
                            x={x}
                            y={y - 4}
                            fill={strokeColor}
                            fontSize={18}
                        >
                            {detectedObject.labelName} :{" "}
                            {detectedObject.trackerId}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}
